import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { User } from '@/types'
import {
  canUseGlobalView,
  parsePrimaryRegion,
  parseUserRegionCodes,
  resolveAvailableRegions,
  type RegionCode,
  type RegionView,
} from '@/config/regions'

interface RegionState {
  /** Regions the signed-in user may see (intersection with configured APIs). */
  availableRegions: RegionCode[]
  /** Active UI filter: IN | GCC | GLOBAL. */
  view: RegionView
  syncFromUser: (user: User | null) => void
  setView: (view: RegionView) => void
  /** Regions to hit for the current view. */
  activeRegions: () => RegionCode[]
}

function pickDefaultView(
  available: RegionCode[],
  preferred: RegionView | null,
  primary: RegionCode | null
): RegionView {
  if (preferred === 'GLOBAL' && canUseGlobalView(available)) return 'GLOBAL'
  if (preferred && preferred !== 'GLOBAL' && available.includes(preferred)) return preferred
  if (primary && available.includes(primary)) return primary
  if (canUseGlobalView(available)) return 'GLOBAL'
  return available[0] ?? 'IN'
}

export const useRegionStore = create<RegionState>()(
  persist(
    (set, get) => ({
      availableRegions: [],
      view: 'IN',

      syncFromUser: (user) => {
        const entitled = parseUserRegionCodes(user)
        const available = resolveAvailableRegions(entitled)
        const primary = parsePrimaryRegion(user)
        const nextView = pickDefaultView(available, get().view, primary)
        set({ availableRegions: available, view: nextView })
      },

      setView: (view) => {
        const { availableRegions } = get()
        if (view === 'GLOBAL') {
          if (!canUseGlobalView(availableRegions)) return
          set({ view: 'GLOBAL' })
          return
        }
        if (!availableRegions.includes(view)) return
        set({ view })
      },

      activeRegions: () => {
        const { view, availableRegions } = get()
        if (view === 'GLOBAL') return availableRegions.filter((r) => r === 'IN' || r === 'GCC')
        return availableRegions.includes(view) ? [view] : availableRegions.slice(0, 1)
      },
    }),
    {
      name: 'rfid-tracking-region-storage',
      partialize: (s) => ({ view: s.view }),
    }
  )
)
